import React, { useEffect, useState } from 'react'
import { Avatar, Col, DatePicker, Form, FormInstance, Input, Row, Select } from 'antd'
import FileUploader from 'helper/FileUploader'
import GenderSelect from 'helper/GenderSelect'
import AllergyList from 'helper/AllergyList'
import GradeInput from 'pages/Family/Profiles/FamilyProfile/FamilyMembers/Modals/Kid/ModalContent/GradeInput'
import {
  feetAndInchesToMeters,
  feetToMeters,
  inchesToMeters,
  kidFeetDropdown,
  kidInchesDropdown,
} from 'pages/Family/Profiles/FamilyProfile/FamilyMembers/Modals/Kid/ModalContent/constants'
import { validateAlphabets } from 'helper/utility'
import { CreateKidPayload } from 'types/family/kid'
import { useCreateKid } from 'api/family/kid'
import { Allergy } from 'types/globalTypes'
import ButtonSpinner from 'components/global/ButtonSpinner'
import moment from 'moment'

interface Props {
  familyId: string
  onSuccess: (kidId: string) => void
  onBack: () => void
  externalLoading?: boolean
}

const DropInAddKidForm: React.FC<Props> = ({ familyId, onSuccess, onBack, externalLoading }) => {
  const [form] = Form.useForm()
  const [avatar, setAvatar] = useState<string>('')
  const [feet, setFeet] = useState<number | undefined>()
  const [inches, setInches] = useState<number | undefined>()
  const [meter, setMeter] = useState<number | undefined>()
  const [weightInKg, setWeightInKg] = useState<number | undefined>()
  const [allergies, setAllergies] = useState<Allergy[]>([])

  const { mutate: createKid, isLoading } = useCreateKid()

  useEffect(() => {
    if (feet !== undefined && inches !== undefined) {
      setMeter(feetAndInchesToMeters(feet, inches))
    } else if (feet === undefined && inches !== undefined) {
      setMeter(inchesToMeters(inches))
    } else if (feet !== undefined && inches === undefined) {
      setMeter(feetToMeters(feet))
    }
  }, [feet, inches])

  const onFinish = (values: CreateKidPayload) => {
    const payload: CreateKidPayload = {
      ...values,
      dob: values.dob ? moment(values.dob).format('YYYY-MM-DD') : undefined,
      weight: weightInKg,
      height: meter || undefined,
      allergies,
    }
    createKid(
      { familyId, payload },
      { onSuccess: (data: any) => onSuccess(data._id) },
    )
  }

  const busy = isLoading || !!externalLoading

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
      {/* Photo */}
      <Row gutter={24} align="middle" className="mb-10px">
        <Col span={4}><Avatar size={64} src={avatar || undefined} /></Col>
        <Col span={18}>
          <Form.Item name="photoId">
            <FileUploader setAvatar={setAvatar} name="drop-in-kid" form={form} fileId={undefined} />
          </Form.Item>
        </Col>
      </Row>

      {/* First + Middle name */}
      <Row gutter={24}>
        <Col span={12}>
          <label className="input-label required-input">First Name</label>
          <Form.Item name="firstName" rules={[{ required: true, message: 'Required' }, { validator: validateAlphabets }]}>
            <Input placeholder="First name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <label className="input-label">Middle Name</label>
          <Form.Item name="middleName">
            <Input placeholder="Middle name" />
          </Form.Item>
        </Col>
      </Row>

      {/* Last + Preferred name */}
      <Row gutter={24}>
        <Col span={12}>
          <label className="input-label required-input">Last Name</label>
          <Form.Item name="lastName" rules={[{ required: true, message: 'Required' }, { validator: validateAlphabets }]}>
            <Input placeholder="Last name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <label className="input-label">Preferred Name</label>
          <Form.Item name="nickName">
            <Input placeholder="Preferred name" />
          </Form.Item>
        </Col>
      </Row>

      {/* Gender */}
      <Row>
        <Col span={24}>
          <label className="input-label required-input">Gender</label>
          <Form.Item name="gender" rules={[{ required: true, message: 'Required' }]}>
            <GenderSelect form={form} data={undefined} />
          </Form.Item>
        </Col>
      </Row>

      {/* DOB */}
      <Row>
        <Col span={24}>
          <label className="input-label required-input">Date of Birth</label>
          <Form.Item name="dob" rules={[{ required: true, message: 'Required' }]}>
            <DatePicker className="selector w-full" />
          </Form.Item>
        </Col>
      </Row>

      {/* School */}
      <Row>
        <Col span={24}>
          <label className="input-label">School</label>
          <Form.Item name="school">
            <Input placeholder="School name" />
          </Form.Item>
        </Col>
      </Row>

      {/* Grade */}
      <Row>
        <Col span={24}>
          <label className="input-label">Grade</label>
          <Form.Item name="gradeId">
            <GradeInput form={form} gradeId="" data={undefined} />
          </Form.Item>
        </Col>
      </Row>

      {/* Weight + Height */}
      <Row gutter={24}>
        <Col span={12}>
          <label className="input-label">Weight (lbs)</label>
          <Form.Item name="weight">
            <Input
              placeholder="0"
              type="number"
              className="custom-input-field"
              onChange={e => {
                const lbs = parseFloat(e.target.value)
                setWeightInKg(isNaN(lbs) ? undefined : lbs * 0.453592)
              }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <label className="input-label">Height</label>
          <Form.Item name="height">
            <div className="combined-input-field flex justify-between">
              <div className="d-flex align-items-center">
                <Select
                  className="combined-select"
                  value={feet}
                  onChange={(v: number) => setFeet(v)}
                  placeholder="ft"
                >
                  {kidFeetDropdown.map((o: any) => (
                    <Select.Option key={o.key} value={o.key}>{o.label}</Select.Option>
                  ))}
                </Select>
                <span className="separator">&nbsp;ft</span>
              </div>
              <div className="d-flex align-items-center">
                <Select
                  className="combined-select"
                  value={inches}
                  onChange={(v: number) => setInches(v)}
                  placeholder="in"
                >
                  {kidInchesDropdown.map((o: any) => (
                    <Select.Option key={o.key} value={o.key}>{o.label}</Select.Option>
                  ))}
                </Select>
                <span className="separator">&nbsp;in</span>
              </div>
            </div>
          </Form.Item>
        </Col>
      </Row>

      {/* Allergies */}
      <Row>
        <Col span={24}>
          <label className="input-label">Allergies</label>
          <Form.Item name="allergies" className="form-control allergies-field">
            <AllergyList form={form} setAllergies={setAllergies} data={[]} />
          </Form.Item>
        </Col>
      </Row>

      {/* Medical Notes */}
      <Row>
        <Col span={24}>
          <label className="input-label">Medical Notes</label>
          <Form.Item name="medicalNotes">
            <Input placeholder="Ex.: Asthma, Lactose intolerant" />
          </Form.Item>
        </Col>
      </Row>

      {/* Special Needs */}
      <Row>
        <Col span={24}>
          <label className="input-label">Special Needs</label>
          <Form.Item name="specialNeeds">
            <Input placeholder="Special needs" />
          </Form.Item>
        </Col>
      </Row>

      <div className="flex gap-10px mt-20px">
        <button type="submit" className="btn-prim" disabled={busy}>
          {busy ? <ButtonSpinner size={20} color="#fff" /> : 'Create kid & Check Out'}
        </button>
        <button type="button" className="btn-borderless" onClick={onBack} disabled={busy}>
          Back
        </button>
      </div>
    </Form>
  )
}

export default DropInAddKidForm
